import { useEffect, useState } from 'react';

/**
 * `true` abajo de 640px — el breakpoint `sm` de Tailwind.
 *
 * Es el corte que usan las superficies "de teléfono" de la app (el hero del inicio, el
 * sheet de Generar, los títulos de página), así que una pantalla que tiene que ELEGIR
 * entre dos componentes según el dispositivo —y no sólo mostrarlos u ocultarlos con
 * CSS— tiene que preguntar por este mismo ancho. Con `useIsDesktop` (1024px) quedaría
 * una franja de 640 a 1024 montando el componente de mobile, que ahí es `sm:hidden`:
 * invisible, y el tap sin efecto.
 *
 * Reactivo: se actualiza al cruzar el breakpoint (resize / rotación).
 */
const QUERY = '(max-width: 639px)';

/** El breakpoint `md` de Tailwind, por abajo. Ver `useDebajoDeMd`. */
const QUERY_MD = '(max-width: 767px)';

/**
 * `true` mientras la media query dé positivo. Reactivo al resize y a la rotación.
 *
 * `congelado` lo deja clavado en el valor que tenía: se deja de escuchar. Es para las
 * pantallas donde cambiar de layout DESMONTA algo que no se puede remontar sin costo —
 * el formulario de tarjeta embebido de `/alta-empresa`, que al remontarse pierde lo que
 * el cliente venía tipeando y abre una segunda sesión de checkout contra el store de
 * módulo del SDK de Rebill, que es único y compartido. Default `false`: los usos que ya
 * había siguen exactamente igual. Al descongelar, el `onChange()` de abajo (que ya
 * estaba) re-sincroniza el valor con el viewport real de una.
 * Ver ADR-2026-09-22-un-solo-arbol-en-el-alta-por-link.
 */
function useCoincideMedia(query: string, congelado = false): boolean {
  const [coincide, setCoincide] = useState(
    () =>
      typeof window !== 'undefined' &&
      typeof window.matchMedia === 'function' &&
      window.matchMedia(query).matches,
  );

  useEffect(() => {
    // Congelado = ni siquiera nos suscribimos: el valor se queda como está. Al
    // descongelar volvemos a entrar acá y el `onChange()` de abajo lo pone al día con
    // el viewport real (el usuario puede haber rotado el teléfono mientras tanto).
    if (congelado) return;
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return;
    const mql = window.matchMedia(query);
    const onChange = () => setCoincide(mql.matches);
    onChange();
    mql.addEventListener('change', onChange);
    return () => mql.removeEventListener('change', onChange);
  }, [query, congelado]);

  return coincide;
}

export function useEsMobile(): boolean {
  return useCoincideMedia(QUERY);
}

/**
 * `true` abajo de 768px — el breakpoint `md`.
 *
 * Es el corte de las pantallas de MODO: su layout de dos columnas es `hidden md:flex` y
 * el de teléfono, `md:hidden`. Una pantalla que elige entre dos componentes según el
 * dispositivo tiene que preguntar por ESTE ancho y no por el de `useEsMobile` (639px):
 * entre 640 y 767 montaría el de escritorio, que ahí todavía está oculto por CSS — o
 * sea, pantalla en blanco.
 */
export function useDebajoDeMd(congelado = false): boolean {
  return useCoincideMedia(QUERY_MD, congelado);
}

/**
 * `true` si el dispositivo tiene un puntero que hace hover (mouse, trackpad) y la pantalla
 * mide `md` o más. Es lo que decide si un dato puede vivir en algo que aparece AL PASAR el
 * mouse: en el teléfono y en una tablet táctil no hay hover, así que ahí tiene que estar a
 * la vista (ADR-2026-10-15-el-costo-de-la-inteligencia-se-ve-al-pasar-el-mouse).
 */
export function usePuedeHover(): boolean {
  return useCoincideMedia('(hover: hover) and (min-width: 768px)');
}

export default useEsMobile;
